import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trophy, Upload, ImageIcon, Loader2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MarkWonModal({ leadId, open, onOpenChange, onSuccess }) {
  const [logoOption, setLogoOption] = useState("text"); // "upload" | "text"
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [marking, setMarking] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/svg+xml", "image/png", "image/jpeg", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload an SVG, PNG, JPG, or WebP file");
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Maximum 5MB allowed.");
      return;
    }

    setSelectedFile(file);
    setLogoOption("upload");
  };

  const handleMarkWon = async () => {
    setMarking(true);
    
    try {
      // First, mark as won
      await axios.post(`${API}/leads/${leadId}/mark-won`, null, {
        params: { logo_provided: logoOption === "upload" }
      });

      // If logo was selected, upload it
      if (logoOption === "upload" && selectedFile) {
        setUploading(true);
        const formData = new FormData();
        formData.append("file", selectedFile);
        
        await axios.post(`${API}/leads/${leadId}/upload-logo`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }

      toast.success("Lead marked as Won!");
      onSuccess?.();
      onOpenChange(false);
      
      // Reset state
      setSelectedFile(null);
      setLogoOption("text");
    } catch (error) {
      console.error("Failed to mark won:", error);
      toast.error(error.response?.data?.detail || "Failed to mark lead as won");
    } finally {
      setMarking(false);
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!marking && !uploading) {
      setSelectedFile(null);
      setLogoOption("text");
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="text-yellow-500" size={20} />
            Mark Lead as Won
          </DialogTitle>
          <DialogDescription>
            Congratulations! Would you like to upload the client's logo now?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Option 1: Upload Logo */}
          <div 
            className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
              logoOption === "upload" ? "border-primary bg-primary/5" : "hover:border-muted-foreground/50"
            }`}
            onClick={() => {
              setLogoOption("upload");
              if (!selectedFile) {
                fileInputRef.current?.click();
              }
            }}
          >
            <Checkbox 
              checked={logoOption === "upload"} 
              onCheckedChange={() => {
                setLogoOption("upload");
                if (!selectedFile) {
                  fileInputRef.current?.click();
                }
              }}
            />
            <div className="flex-1">
              <Label className="cursor-pointer font-medium">
                Client provided logo — Upload now
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Upload the logo to use in the full site build
              </p>
              
              {selectedFile && (
                <div className="mt-2 flex items-center gap-2 text-sm text-green-600">
                  <Check size={14} />
                  {selectedFile.name}
                </div>
              )}
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
            >
              <Upload size={14} className="mr-1" />
              {selectedFile ? "Change" : "Browse"}
            </Button>
          </div>

          {/* Option 2: Text/Monogram */}
          <div 
            className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
              logoOption === "text" ? "border-primary bg-primary/5" : "hover:border-muted-foreground/50"
            }`}
            onClick={() => setLogoOption("text")}
          >
            <Checkbox 
              checked={logoOption === "text"} 
              onCheckedChange={() => setLogoOption("text")}
            />
            <div>
              <Label className="cursor-pointer font-medium">
                Proceed with text/monogram for now
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                You can upload a logo later from the Brand Assets section
              </p>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".svg,.png,.jpg,.jpeg,.webp"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button 
            variant="outline" 
            onClick={handleClose}
            disabled={marking || uploading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleMarkWon}
            disabled={marking || uploading || (logoOption === "upload" && !selectedFile)}
          >
            {marking || uploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {uploading ? "Uploading..." : "Saving..."}
              </>
            ) : (
              <>
                <Trophy className="mr-2 h-4 w-4" />
                Mark as Won
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
